// The RFP summary card under a Home chat answer (Lane 1368, slice S2; plan B3.4).
// Every word on it was written by the server from the saved RFP draft; the buyer
// builds the draft event here (or says yes in their next message). Nothing is sent
// to any supplier. Home.js only composes this component.
// Lane 1375 (Ryan's QA): the goal is Wingman's clean statement, editable here before
// Build (the edit goes with the Build request); no "Not checked" item; the parts shown
// are the largest by spend; one plain line for what is still open; a weekend date is
// noted.
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE } from '../../config';
import { formatMoney, formatWindow } from './sourcingApi';
import { GOAL_MAX_OWN_WORDS, GOAL_RAW_MAX, cleanGoal, goalLength } from './goal';
import './SourcingIntakeCard.css';

const REFUSAL_TEXT = {
  card_replaced: 'This card was replaced by a newer one. Use the newest card.',
  intake_stale: 'The parts or their figures changed since this card. Ask again in chat for a fresh card.',
  intake_not_ready: 'This RFP draft can no longer be built. Ask again in chat.',
  card_expired: 'This card is more than a day old. Ask again in chat for a fresh one.',
};

async function post(authFetch, path, body) {
  const r = await authFetch(`${API_BASE}${path}`, {
    method: 'POST', headers: { 'Content-Type': 'application/json' },
    body: body === undefined ? undefined : JSON.stringify(body),
  });
  const data = await r.json().catch(() => null);
  if (!r.ok) {
    const detail = data && data.detail;
    const code = detail && typeof detail === 'object' ? detail.code : null;
    const message = REFUSAL_TEXT[code] || (detail && typeof detail === 'object' && detail.message)
      || (r.status === 404 ? 'This RFP draft is no longer available.' : 'Something went wrong. Try again.');
    const err = new Error(message);
    err.code = code;
    throw err;
  }
  return data;
}

// Lane 1439: the same calls and refusal words for the version 2 card.
export const postIntake = post;

export function SourcingIntakeBuilt({ built }) {
  const navigate = useNavigate();
  if (!built || !built.event_public_id) return null;
  return (
    <div className="hm-rptoffer srce-intake" data-testid="srce-intake-built">
      <button type="button" className="hm-rptoffer-btn" onClick={() => navigate(`/sourcing/${built.event_public_id}`)}>
        Open the draft event
      </button>
    </div>
  );
}

// One label and its value. The label has its own column and the value starts after a
// fixed gap, so a long label ("Suppliers to invite") never runs into its value (Lane 1372).
function IntakeItem({ label, children }) {
  return (
    <li className="srce-intake-item">
      <span className="srce-intake-k">{label}</span>
      <div className="srce-intake-v">{children}</div>
    </li>
  );
}

// Lane 1438: the drafts a buyer chose to start a new RFP beside, per RFP draft, for
// this page: a newer card of the same draft does not ask again.
export const startedNew = new Set();

// One line per open draft that may be this same RFP (Lane 1438, one draft per purpose).
export function draftReason(d) {
  return d.reason === 'same_supplier' ? 'built from this supplier\'s parts'
    : `${Number(d.shared_parts || 0).toLocaleString()} of these parts`;
}

export default function SourcingIntakeCard({ card, conversationId, authFetch }) {
  const navigate = useNavigate();
  const [state, setState] = useState('open');   // open | building | dismissed
  const openDrafts = card.open_drafts || [];
  const [startNew, setStartNew] = useState(startedNew.has(card.intake_id));
  const askFirst = openDrafts.length > 0 && !startNew;
  const [error, setError] = useState('');
  // Lane 1375 (Ryan, R2): Wingman's goal statement, which the buyer may edit before Build.
  const [goal, setGoal] = useState(card.goal || '');
  const p = card.parts || {};
  const cleaned = cleanGoal(goal, true);
  const edited = cleaned !== cleanGoal(card.goal || '', true);
  const goalLen = goalLength(cleaned);
  const goalBad = !cleaned || goalLen > GOAL_MAX_OWN_WORDS;

  const build = async () => {
    if (goalBad) return;
    setState('building');
    setError('');
    try {
      const out = await post(authFetch, '/api/sourcing/intakes/build', {
        summary_token: card.token, conversation_id: conversationId,
        ...(edited ? { goal: cleaned } : {}),
      });
      navigate(`/sourcing/${out.event_public_id}`);
    } catch (e) {
      setError(e.message);
      setState('open');
    }
  };
  const notNow = async () => {
    setError('');
    try {
      await post(authFetch, `/api/sourcing/intakes/${encodeURIComponent(card.intake_id)}/dismiss`);
      setState('dismissed');
    } catch (e) {
      setError(e.message);
    }
  };

  if (state === 'dismissed') {
    return <div className="srce-intake srce-intake--quiet">RFP draft put aside. Ask again in chat to start one.</div>;
  }
  // Lane 1387 (Ryan): the card waits for the buyer, so it reads as something to act on:
  // a blue accent border, and "Build the draft" as the blue primary button.
  return (
    <section className="srce-intake srce-intake--act" data-testid="srce-intake-card" aria-label="RFP summary">
      {/* Lane 1438: the title is the event's name, exactly (one value). */}
      <div className="srce-intake-sub">New RFP draft</div>
      <div className="srce-intake-t">{card.name}</div>
      {askFirst && (
        <div className="srce-intake-drafts" role="status" data-testid="srce-intake-open-drafts">
          <div>You already have {openDrafts.length === 1 ? 'a draft RFP that may be this one' : 'draft RFPs that may be this one'}:</div>
          <ul className="srce-intake-rules">
            {openDrafts.map((d) => (
              <li key={d.event_public_id}>
                <span className="srce-intake-goal">{d.name}</span> ({draftReason(d)})
                {' '}<button type="button" className="hm-rptoffer-btn" onClick={() => navigate(`/sourcing/${d.event_public_id}`)}>Open it</button>
              </li>
            ))}
          </ul>
          <div className="srce-intake-row">
            <button type="button" className="hm-rptoffer-btn"
              onClick={() => { startedNew.add(card.intake_id); setStartNew(true); }}>Start a new one</button>
          </div>
        </div>
      )}
      <ul className="srce-intake-list">
        <IntakeItem label="Parts">
          {Number(p.parts || 0).toLocaleString()} {p.parts === 1 ? 'part' : 'parts'}
          {' '}({Number(p.lines || 0).toLocaleString()} part-and-site {p.lines === 1 ? 'line' : 'lines'})
          {' '}at {Number(p.site_count || 0).toLocaleString()} {p.site_count === 1 ? 'site' : 'sites'}
          {(p.examples || []).length > 0 && (
            <div className="srce-intake-sub">Largest by spend: {p.examples.join(', ')}</div>
          )}
        </IntakeItem>
        <IntakeItem label="Spend">
          {formatMoney(p.spend)} spend on these parts, all suppliers, {p.window_text || formatWindow(p.window)}
          {p.supplier_name && <> ({formatMoney(p.supplier_spend)} with {p.supplier_name})</>}
        </IntakeItem>
        <IntakeItem label="Goal">
          <textarea className="srce-intake-goal-input" value={goal} rows={3} maxLength={GOAL_RAW_MAX}
            aria-label="Goal" disabled={state === 'building'} onChange={(e) => setGoal(e.target.value)} />
          {goalBad ? (
            <div className="srce-intake-sub srce-intake-warn" role="alert">
              {cleaned ? `Shorten the goal to ${GOAL_MAX_OWN_WORDS.toLocaleString()} characters to build.`
                : 'Write a goal to build the draft.'}
            </div>
          ) : (
            <div className="srce-intake-sub">
              {edited ? 'Your edit is used when you click Build the draft.' : 'Written by Wingman from what you said. Edit it if you like.'}
            </div>
          )}
          {card.goal_words && (
            <div className="srce-intake-sub">You said: <span className="srce-intake-goal">{card.goal_words}</span></div>
          )}
        </IntakeItem>
        {/* The mapping was made for Wingman's goal: an edited goal is built without it. */}
        {!edited && (card.goal_rules || []).length > 0 && (
          <IntakeItem label="Already in place">
            <ul className="srce-intake-rules">
              {card.goal_rules.map((text, i) => <li key={i}>{text}</li>)}
            </ul>
            <div className="srce-intake-sub">Standard rules of every event that already cover part of your goal.</div>
          </IntakeItem>
        )}
        {(card.suppliers || []).length > 0 && (
          <IntakeItem label="Suppliers to invite">
            {card.suppliers.map((x) => x.name).join(', ')}
            <div className="srce-intake-sub">Added to the list only. Nobody is contacted until you publish.</div>
          </IntakeItem>
        )}
        {(card.also_approved || []).length > 0 && (
          <IntakeItem label="Also approved">
            {card.also_approved.join(', ')}
            <div className="srce-intake-sub">Not included. Say a name to add it.</div>
          </IntakeItem>
        )}
        {(card.limits || []).length > 0 && (
          <IntakeItem label="Limits">
            <ul className="srce-intake-rules">
              {card.limits.map((text, i) => <li key={i}>{text}</li>)}
            </ul>
            <div className="srce-intake-sub">These become confirmed rules in the event.</div>
          </IntakeItem>
        )}
        {(card.timing || []).length > 0 && (
          <IntakeItem label="Timing">
            {card.timing.join('; ')}
            {(card.timing_notes || []).map((note) => (
              <div key={note} className="srce-intake-sub">{note}</div>
            ))}
          </IntakeItem>
        )}
      </ul>
      {card.still_open && <div className="srce-intake-sub">{card.still_open}</div>}
      <div className="srce-intake-row">
        {!askFirst && (
          <button type="button" className="hm-rptoffer-btn srce-intake-build" disabled={state === 'building' || goalBad} onClick={build}>
            {state === 'building' ? 'Building\u2026' : 'Build the draft'}
          </button>
        )}
        <button type="button" className="hm-rptoffer-btn" disabled={state === 'building'} onClick={notNow}>Not now</button>
        <span className="srce-intake-sub">{card.note || 'Nothing is sent to any supplier.'}
          {askFirst ? '' : (edited ? ' Click Build the draft to use your edit.' : ' Or say yes in your next message.')}</span>
      </div>
      {error && <div className="hm-rptoffer-fail" role="alert">{error}</div>}
    </section>
  );
}

// After a reload Home chat starts empty (ChatContext), so a live RFP draft would be
// stranded: this banner offers to continue it in the new chat, or to put it aside.
export function SourcingIntakeBanner({ authFetch, conversationId, onResumed }) {
  const [active, setActive] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  React.useEffect(() => {
    let stop = false;
    authFetch(`${API_BASE}/api/sourcing/intakes/active`).then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!stop && d && d.intake) setActive(d.intake); })
      .catch(() => {});
    return () => { stop = true; };
  }, [authFetch]);
  if (!active || active.conversation_id === conversationId) return null;
  const resume = async () => {
    setBusy(true);
    setError('');
    try {
      const out = await post(authFetch, `/api/sourcing/intakes/${encodeURIComponent(active.intake_id)}/resume`,
        { conversation_id: conversationId });
      setActive(null);
      onResumed(out);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  const discard = async () => {
    setBusy(true);
    try {
      await post(authFetch, `/api/sourcing/intakes/${encodeURIComponent(active.intake_id)}/dismiss`);
      setActive(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="srce-intake srce-intake-banner" role="status" data-testid="srce-intake-banner">
      <span>You have an RFP in progress: {active.name}.</span>
      <button type="button" className="hm-rptoffer-btn" disabled={busy} onClick={resume}>Continue</button>
      <button type="button" className="hm-rptoffer-btn" disabled={busy} onClick={discard}>Discard</button>
      {error && <span className="hm-rptoffer-fail" role="alert">{error}</span>}
    </div>
  );
}
